import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/lib/hooks";
import PageHeader from "@/components/PageHeader";

export const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(
      { tr: "tr-TR", en: "en-US", ru: "ru-RU", ar: "ar-EG" }[lang] || "tr-TR",
      { year: "numeric", month: "long", day: "numeric" }
    );
  } catch {
    return "";
  }
};

export default function Blog() {
  const { t, ml, lang } = useLang();
  const { data: posts = [], isLoading } = useContent("/content/posts");

  return (
    <div data-testid="blog-page">
      <PageHeader title={t("blog.title")} subtitle={t("blog.subtitle")} testId="blog-title" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {isLoading ? (
          <p className="text-zinc-500">{t("common.loading")}</p>
        ) : posts.length === 0 ? (
          <p className="text-zinc-500" data-testid="blog-empty">{t("blog.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group overflow-hidden rounded-2xl border border-zinc-200 bg-white transition-all duration-300 hover:border-[#1A4BFF] hover:shadow-[0_12px_40px_-16px_rgba(26,75,255,0.35)]"
              >
                {post.image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={ml(post.title)}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-7">
                  <p className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(post.created_at, lang)}
                  </p>
                  <h3 className="mt-3 font-heading text-lg font-bold text-zinc-950 group-hover:text-[#1A4BFF]">{ml(post.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600 line-clamp-3">{ml(post.excerpt)}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1A4BFF]">
                    {t("blog.readMore")}
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
